import { LitElement, html, css, t } from '@hh/global.js'

class HHInfiniteSlider extends LitElement {
  static properties = {
    arrows: {
      type: Boolean,
      reflect: true,
    },
    _scrolling: {
      state: true,
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
  }

  constructor() {
    super()

    this.intersectionObserverEntries = new Map()
    this.activeSlide = 0
    this._scrolling = false
  }

  get items() {
    const slot = this.shadowRoot?.querySelector(`slot[name='item']`)
    return slot?.assignedNodes() ?? null
  }

  get lightDomItems() {
    return this.querySelectorAll('[slot="item"]') ?? []
  }

  get slideContainer() {
    return this.shadowRoot?.querySelector('.slides') ?? null
  }

  connectedCallback() {
    super.connectedCallback()

    this.createClones()

    this.intersectionObserver = this.initIntersectionObserver()
    this.intersectionObserver.takeRecords().forEach((entry) => {
      this.intersectionObserverEntries.set(entry.target, entry)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.intersectionObserver.disconnect()
  }

  firstUpdated() {
    this.items.forEach((item) => this.intersectionObserver.observe(item))
  }

  onClick(e) {
    const { name } = e.currentTarget
    this.move(name)
  }

  move(direction) {
    this._scrolling = true
    const slideScrollPosition =
      direction === 'next'
        ? this.slideContainer.scrollLeft + this.items[0].clientWidth
        : this.slideContainer.scrollLeft - this.items[0].clientWidth

    this.slideContainer.scrollTo({
      left: slideScrollPosition,
      behavior: 'smooth',
    })
  }

  moveTo(index, behavior = 'auto') {
    this._scrolling = true
    const slideScrollPosition = this.items[0].clientWidth * index
    this.slideContainer.scrollTo({
      left: slideScrollPosition,
      behavior: behavior,
    })
  }

  onScroll() {
    const entries = [...this.intersectionObserverEntries.values()]
    const firstIntersecting = entries.find((entry) => entry.isIntersecting)

    if (firstIntersecting?.target.hasAttribute('data-clone')) {
      const clonePosition = firstIntersecting.target.getAttribute('data-clone')
      this.moveTo(
        clonePosition === 'first' ? 1 : this.items.length - 2,
        'instant'
      )
    }

    if (firstIntersecting) {
      this.activeSlide = this.items.indexOf(firstIntersecting.target)
    }

    this._scrolling = false
  }

  createClones() {
    const firstItem = this.lightDomItems[0]
    const lastItem = this.lightDomItems[this.lightDomItems.length - 1]

    const firstClone = firstItem.cloneNode(true)
    const lastClone = lastItem.cloneNode(true)

    firstClone.setAttribute('data-clone', 'first')
    lastClone.setAttribute('data-clone', 'last')

    this.append(firstClone)
    this.prepend(lastClone)

    setTimeout(() => this.moveTo(1, 'instant'), 0)
  }

  initIntersectionObserver() {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const { target } = entry
          this.intersectionObserverEntries.set(target, entry)

          target.toggleAttribute('inert', !entry.isIntersecting)
          target.setAttribute(
            'aria-hidden',
            entry.isIntersecting ? 'false' : 'true'
          )
        })
      },
      {
        root: this,
        threshold: 0.6,
      }
    )
  }

  arrowTemplate(direction) {
    return html`
      <button
        class="arrow arrow--${direction}"
        @click=${this.onClick}
        name="${direction}"
        aria-label="${direction === 'next'
          ? t('accessibility.next_slide')
          : t('accessibility.previous_slide')}"
        aria-controls="slides"
      >
        <slot name="${direction}-arrow"></slot>
      </button>
    `
  }

  render() {
    return html`
      <div class="container">
        ${this.arrows ? this.arrowTemplate('prev') : ''}
        <div
          id="slides"
          class="slides"
          aria-atomic="true"
          aria-busy="${this._scrolling ? 'true' : 'false'}"
          @scroll=${this.onScroll}
        >
          <slot name="item"></slot>
        </div>
        ${this.arrows ? this.arrowTemplate('next') : ''}
      </div>
    `
  }

  static styles = css`
    :host {
      display: block;
      padding: 0 var(--hh-spacing-xs);
      background: var(--hh-background, transparent);
    }

    @media screen and (min-width: 1024px) {
      :host {
        padding: 0;
      }
    }

    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .container {
      position: relative;
    }

    .slides {
      display: flex;
      gap: 10rem;
      align-items: center;
      overflow-x: hidden;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      padding: 0 var(--hh-arrow-size, 0);
    }

    .slides::-webkit-scrollbar {
      display: none;
    }

    ::slotted([slot='item']) {
      width: 100%;
      flex-shrink: 0;
      scroll-snap-align: center;
    }

    .arrow {
      appearance: none;
      background: var(--hh-background, transparent);
      border: none;
      padding: 0;
      color: var(--hh-color, inherit);
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
      z-index: 1;
      height: 100%;
    }

    .arrow:focus-visible {
      outline: 0.2rem solid var(--hh-color-focus);
    }

    .arrow--prev {
      left: -0.2rem;
    }

    .arrow--next {
      right: -0.2rem;
    }

    @media screen and (min-width: 640px) and (max-width: 1399px) {
      .arrow--prev {
        left: -0.5rem;
      }

      .arrow--next {
        right: 0.5rem;
      }
    }

    ::slotted([slot='prev-arrow']),
    ::slotted([slot='next-arrow']) {
      width: var(--hh-arrow-size, 3.2rem);
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `
}

customElements.define('hh-infinite-slider', HHInfiniteSlider)
